<template>
  <v-container fluid>
    <alert-unsaved-changes v-if="unsavedChanges"></alert-unsaved-changes>
    <v-progress-linear
      v-show="loading || resourceLoading"
      color="neutral400"
      indeterminate
      rounded
      height="3"
    ></v-progress-linear>
    <v-form
      v-show="!loading && !resourceLoading"
      ref="form"
      v-model="valid"
      :lazy-validation="false"
    >
      <v-row>
        <v-col cols="12" sm="12" md="6">
          <label>Manufacturer</label>
          <p class="label-info">Equipment manufacturer if resource category is set to equipment</p>
          <v-text-field
            v-model="resource.manufacturer"
            outlined
            :placeholder="'Manufacturer'"
            :rules="minMaxLengthRule(1, 50)"
            class="required"
            data-cy="manufacturer"
            :disabled="!edit"
          ></v-text-field>
        </v-col>
        <v-col cols="12" sm="12" md="6">
          <label>{{ 'Model and Type' }}</label>
          <p class="label-info">
            Model and type (brand) of equipment if resource category is set to equipment
          </p>
          <v-text-field
            v-model="resource.model"
            outlined
            :placeholder="'Model'"
            :disabled="!edit"
            :rules="minMaxLengthRule(1, 100)"
            class="required"
            data-cy="model"
          ></v-text-field>
        </v-col>
        <v-col v-if="!next" cols="12">
          <v-expansion-panels>
            <v-expansion-panel :key="1">
              <v-expansion-panel-header>
                {{ $t('optional-info') }}
              </v-expansion-panel-header>
              <v-expansion-panel-content class="pt-10">
                <v-row>
                  <v-col cols="12" sm="12" md="6">
                    <label>Operational Mode</label>
                    <p class="label-info">
                      How does the equipment operate? Need additional human intervention? Self
                      operated etc.
                    </p>
                    <v-autocomplete
                      v-model="resource.operationalMode"
                      :items="operationalModes"
                      :item-value="'id'"
                      :item-text="'name'"
                      return-object
                      :placeholder="'Select'"
                      outlined
                      :disabled="!edit"
                      clearable
                      data-cy="operationalMode"
                    ></v-autocomplete>
                  </v-col>
                  <v-col cols="12">
                    <v-row>
                      <v-col cols="12">
                        <label>Availability Policy</label>
                        <p class="label-info">
                          When the equipment is available for external/affiliated users
                        </p>
                      </v-col>
                    </v-row>
                    <v-row
                      v-for="(availabilityPolicy, i) in resource.availabilityPolicies"
                      :key="'policy-' + i"
                    >
                      <v-col cols="10">
                        <v-text-field
                          v-model="availabilityPolicy.text"
                          :placeholder="'Enter a availability policy'"
                          outlined
                          :rules="minMaxLengthRule(1, 200)"
                          class="required"
                          :disabled="!edit"
                          data-cy="availabilityPolicy"
                        ></v-text-field>
                      </v-col>
                      <v-col cols="2">
                        <v-btn v-if="edit" fab small text @click="removeAvailabilityPolicy(i)">
                          <v-icon>
                            $vuetify.icons.trash
                          </v-icon>
                        </v-btn>
                      </v-col>
                    </v-row>
                    <v-row v-if="edit">
                      <v-col cols="12">
                        <v-btn tile text @click="addAvailabilityPolicy()">
                          <v-icon left>
                            $vuetify.icons.plus
                          </v-icon>
                          <span
                            class="text-decoration-underline text-transform-none font-weight-bold"
                          >
                            Add a Availability Policy
                          </span>
                        </v-btn>
                      </v-col>
                    </v-row>
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>
    </v-form>
    <!--BUTTONS-->
    <v-row class="mt-10" v-if="next">
      <v-col cols="6" class="d-flex justify-end">
        <v-btn
          depressed
          rounded
          x-large
          color="black"
          @click="back()"
          outlined
          class="black--text font-weight-bold"
          data-cy="submit"
        >
          <v-icon color="black">$vuetify.icons.back</v-icon>
          {{ $t('back') }}
        </v-btn>
      </v-col>
      <v-col cols="6">
        <v-btn
          depressed
          rounded
          x-large
          color="neutral"
          @click="add()"
          :disabled="!valid"
          class="white--text font-weight-bold"
          data-cy="submit"
          >{{ $t('submit') }}
        </v-btn>
      </v-col>
    </v-row>
    <v-row class="mt-10" v-if="!next && edit">
      <v-col cols="6" class="d-flex justify-end" v-if="!admin">
        <v-btn
          depressed
          rounded
          x-large
          color="black"
          @click="cancel()"
          outlined
          class="black--text font-weight-bold"
          data-cy="submit"
        >
          {{ $t('cancel') }}
        </v-btn>
      </v-col>
      <v-col :cols="admin ? '12' : '6'" :class="admin ? 'text-center' : ''">
        <v-btn
          depressed
          rounded
          x-large
          color="neutral"
          @click="add()"
          :disabled="!valid"
          class="white--text font-weight-bold"
          data-cy="submit"
          >{{ $t('update') }}
        </v-btn>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import EventBus from '@/helpers/event-bus.js';
import util from '@/mixins/util.js';
import AlertUnsavedChanges from '@/components/shared/helpers/AlertUnsavedChanges';
import base from '@/services/base-service.js';

export default {
  name: 'ResourceEquipmentInfo',
  mixins: [util, base],
  props: {
    admin: {
      type: Boolean,
      default: false,
    },
    id: {
      type: [Number, String],
      default: null,
    },
    edit: {
      type: Boolean,
      default: true,
    },
    next: {
      type: Boolean,
      default: true,
    },
  },
  components: {
    AlertUnsavedChanges,
  },
  data() {
    return {
      valid: false,
      loadingComponents: -1,
      unsavedChanges: false,
      resource: {
        id: null,
        manufacturer: null,
        model: null,
        operationalMode: null,
        availabilityPolicies: [],
      },
      operationalModes: [],
      resourceLoading: true,
    };
  },
  mounted() {
    this.getOperationalModes().then(res => {
      this.operationalModes = res.data.content;
      this.loadingComponents++;
    });
  },
  methods: {
    cancel() {
      this.$emit('cancel', true);
    },
    back() {
      console.log('back!');
      this.$emit('tab', 2);
    },
    add() {
      EventBus.$emit('waiting', true);
      this.axios
        .post('/api/v1/resources/equipment-info', this.resource)
        .then(() => {
          console.log('Success');
          EventBus.$emit('snackbar', {
            text: this.$i18n.t('resource-updated'),
          });
          this.unsavedChanges = false;
          if (this.resource.draft) {
            this.$router.push({
              name: 'Services',
            });
          }
          this.$emit('tab', 4);
        })
        .catch(error => {
          console.log({ error });
        })
        .finally(() => {
          EventBus.$emit('waiting', false);
        });
    },
    addAvailabilityPolicy() {
      this.resource.availabilityPolicies.push({ text: null });
    },
    removeAvailabilityPolicy(index) {
      this.resource.availabilityPolicies.splice(index, 1);
    },
  },
  computed: {
    loading() {
      return this.loadingComponents !== 0;
    },
  },
  watch: {
    loading(value) {
      if (value === false) {
        console.log('Loading');
        if (this.id != null) {
          this.axios
            .get('/api/v1/resources/' + this.id)
            .then(res => {
              this.resource = res.data;
              this.$nextTick(() => {
                this.resourceLoading = false;
              });
            })
            .catch(error => {
              console.log({ error });
            });
        } else {
          this.resourceLoading = false;
        }
      }
    },
    resource: {
      handler: function(val, oldVal) {
        console.log('[Resource] Value changed');
        if (oldVal.id) {
          this.unsavedChanges = true;
        }
      },
      deep: true,
    },
  },
};
</script>
